import { Element, useNode } from "@craftjs/core";
import * as React from "react";
import { Actions } from "src/types";
import { ThemeContext } from "styled-components";

import TopLevelFloatingOptions from "@/components/editor/block-outline/floating-options/top-level-floating-component";
import type { ImagePosition } from "@/components/editor/image-positioner/image-positioner-component";
import Button from "@/components/editor/primitives/button/button-component";
import RichText from "@/components/editor/primitives/rich-text";
import Text from "@/components/editor/primitives/text/text-component";
import {
  BlockBackgroundColorWrapper,
  BlockMaxWidthWrapper,
} from "@/components/editor/shared/block-wrapper";
import { aquaLogo, whiteLogo } from "@/components/editor/shared/header/assets";
import formatLogoSize from "@/components/editor/shared/header/format-logo-size";
import HeaderSettings, {
  ImageFit,
} from "@/components/editor/shared/header/header-settings-component";
import StyledButtonContainer from "@/components/editor/themes/hazel/shared/cta-button";
import { ColorLabels } from "@/components/editor/utils/colors";
import useEvent from "@/hooks/use-event";
import useRichTextCompatible from "@/hooks/use-rich-text-compatible";
import humanizeDateRange from "@/utils/dates/humanizeDateRange";

import {
  DEFAULT_BUTTON_TEXT,
  DEFAULT_HEADING_TEXT,
  DEFAULT_SUBHEADING_TEXT,
} from "./constants";
import {
  Container,
  Content,
  CTATextWrapper,
  Image,
  ImageWrapper,
  Logo,
  LogoWrapper,
  TextContent,
  Wrapper,
} from "./header-option-3-component-styles";

type HeaderOption3Props = {
  backgroundImage?: string;
  backgroundImageAlt?: string;
  backgroundPosition?: ImagePosition;
  ctaText?: string;
  heading?: string;
  isLogoShowing?: boolean;
  logo?: string;
  logoSize?: number;
  imageFit?: ImageFit;
};

const CustomHeaderSettings = () => {
  return (
    <HeaderSettings
      optimalImageSize="1440x780px"
      backgroundImageSize={{
        width: "240",
        height: "130",
      }}
    />
  );
};

export default function HeaderOption3(props: HeaderOption3Props) {
  const {
    connectors: { connect },
    id: nodeId,
  } = useNode();
  const { event } = useEvent();
  const theme = React.useContext(ThemeContext);

  const defaultLogoImage =
    theme.colors[ColorLabels.BACKGROUND] === "#024348" ? aquaLogo : whiteLogo;

  const isRichTextCompatible = useRichTextCompatible(nodeId, [
    "heading",
    "subheading",
    "cta-text",
  ]);

  return (
    <BlockBackgroundColorWrapper ref={connect} id={nodeId}>
      <BlockMaxWidthWrapper maxWidth="1440px">
        <Wrapper>
          <Container>
            <ImageWrapper>
              <Image
                imageFit={props.imageFit}
                $position={props.backgroundPosition || "center center"}
                src={props.backgroundImage}
                alt={props.backgroundImageAlt || ""}
              />
            </ImageWrapper>
            <Content>
              {props.isLogoShowing && (
                <LogoWrapper>
                  <Logo
                    draggable="false"
                    src={props.logo || defaultLogoImage}
                    alt="Header Logo"
                    data-testid="header-option-3-logo"
                    style={{
                      width: formatLogoSize(props.logoSize),
                      height: "auto",
                    }}
                    isLogoResized={!isNaN(props.logoSize)}
                  />
                </LogoWrapper>
              )}
              <TextContent>
                {isRichTextCompatible ? (
                  <>
                    <Element
                      id="heading"
                      is={RichText}
                      text={`<h2 style="text-align:center;">${
                        event.name || DEFAULT_HEADING_TEXT
                      }</h2>`}
                    />
                    <Element
                      id="subheading"
                      is={RichText}
                      text={`<p style="text-align:center;">${DEFAULT_SUBHEADING_TEXT}</p>`}
                    />
                    <CTATextWrapper>
                      <Element
                        id="cta-text"
                        is={RichText}
                        text={`<h5 style="text-align:center;">${humanizeDateRange(
                          event.time_start,
                          event.time_end,
                          event.standard_timezone
                        ).join(" - ")}</h5>`}
                      />
                    </CTATextWrapper>
                  </>
                ) : (
                  <>
                    <Element
                      id="heading"
                      is={Text}
                      element="h1"
                      text={event.name || DEFAULT_HEADING_TEXT}
                      pattern="headingOne"
                    />
                    <Element
                      id="subheading"
                      is={Text}
                      text={DEFAULT_SUBHEADING_TEXT}
                      pattern="paragraph"
                    />
                    <CTATextWrapper>
                      <Element
                        id="cta-text"
                        is={Text}
                        text={humanizeDateRange(
                          event.time_start,
                          event.time_end,
                          event.standard_timezone
                        ).join(" - ")}
                        pattern="headingFour"
                      />
                    </CTATextWrapper>
                  </>
                )}
              </TextContent>
              <StyledButtonContainer>
                <Element
                  id="cta-button"
                  is={Button}
                  text={DEFAULT_BUTTON_TEXT}
                  action={{
                    kind: Actions.TicketSelector,
                  }}
                />
              </StyledButtonContainer>
            </Content>
          </Container>
        </Wrapper>
      </BlockMaxWidthWrapper>
    </BlockBackgroundColorWrapper>
  );
}

HeaderOption3.craft = {
  custom: {
    displayName: "Header option #3",
  },
  related: {
    settings: CustomHeaderSettings,
    leftOutlineOptions: TopLevelFloatingOptions,
  },
};
